/*
 * This module contains functions to access the timeline endpoints.
 * It exposes common behaviour that is shared betweed 2D and 3D timeline tools.
 * Client code should probably use either timeline-api.js oar timeline-3d-api.js
 * and not directly this.
 */

import { ajax } from 'cadenza/api-client/api';
import { getColorWithOpacity } from 'cadenza/utils/colors/colors';
import { resolveReference } from 'cadenza/utils/json';

/**
 * Get timeline data from backend for the given layer IDs
 *
 * @param mapId
 * @param {string[]} layerIds - layer IDs
 * @param {Promise} signal - for cancellation
 * @param {boolean} [allowLargeData] - If true, the soft result count limit is bypassed (but not the hard one)
 * @param {string} [qualifier] - Specify a qualifier that will be sent along the request as an empty argument.
 * The default is empty, which returns timelines for 2D viewer. Currently known qualifier is '3d', returns timelines for 3D viewer.
 * @return {Promise<object>} Promise of timelines data, see net.disy.services.layer.timeline.web.dto.TimelinesDto
 */
export function getTimelinesForLayers (mapId, layerIds, signal, allowLargeData = false, qualifier = false) {
  const params = new URLSearchParams();
  params.append('mapId', mapId);
  if (qualifier) {
    params.append(qualifier, '');
  }
  if (allowLargeData) {
    params.append('allowLargeData', 'true');
  }
  const url = `/layers;layerIds=${layerIds.join()}/timelines?${params.toString()}`;
  return ajax(url, { signal });
}

export function uniqueTimelineId (timeline) {
  return `${timeline.layerId}:timeline:${timeline.timelineId}`;
}

/*
 * The color of the timeline is dependent on the styling of all the points in that track. (this
 * timeline style is for example used in the activity line in the player and in the 3D representation)
 * If the points all have the same foregroundColor then this color will be used for the track. In
 * addition, if all the foregroundOpacities are the same, then we also use this opacity for the track.
 */
export function determineTimelineColor (timeline, originalStyles) {
  const styleReferences = [ ...new Set(timeline.points.map(([ , , style ]) => style.$ref)) ];
  const pointsStyles = styleReferences.map(styleRef => resolveReference(originalStyles, styleRef));

  const timelineColor = getPropertyValueIfSameInAllStyles(pointsStyles, 'foregroundColor');
  if (timelineColor) {
    const timelineColorOpacity = getPropertyValueIfSameInAllStyles(pointsStyles, 'foregroundColorOpacity');
    if (timelineColorOpacity) {
      return getColorWithOpacity(timelineColor, timelineColorOpacity);
    }
  }
  return timelineColor;
}

function getPropertyValueIfSameInAllStyles (styles, propertyName) {
  let propertyValue = false;
  for (let i = 0; i < styles.length; i++) {
    if (styles[i][propertyName]) {
      if (!propertyValue) {
        propertyValue = styles[i][propertyName];
      } else if (propertyValue !== styles[i][propertyName]) {
        return false;
      }
    }
  }
  return propertyValue;
}
